import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  GraduationCap,
  HardHat,
  Wrench,
  Briefcase,
  ShieldCheck,
  Languages,
  QrCode,
  FileText,
  PlayCircle,
  CheckCircle2,
  Activity,
  Users,
  Clock,
  Award,
} from "lucide-react";
import { MasciLogo } from "@/components/MasciLogo";
import { LangToggle } from "@/components/LangToggle";
import { useT } from "@/lib/i18n";
import { useTenant } from "@/lib/useTenant";
import { TRACKS, lessonsForTrack } from "@/data/training";
import { isAdmin } from "@/lib/adminAuth";
import { isPm } from "@/lib/pmAuth";
import { isShop } from "@/lib/shopAuth";
import { isDemoMode } from "@/lib/demoMode";

const ICONS = { HardHat, Wrench, Briefcase, ShieldCheck };

// Operational labels — these are not "courses". They're readiness tracks.
const TRACK_OPS_LABEL = {
  field: { eyebrow: "FIELD CREW · OPERATIONS",      title: "Field Crew Readiness" },
  shop:  { eyebrow: "SHOP & FLEET · READINESS",     title: "Shop & Fleet Readiness" },
  pm:    { eyebrow: "PM · OPERATIONS CONTROL",      title: "PM Operations Control" },
  admin: { eyebrow: "ADMIN / OWNER · COMMAND",      title: "Admin / Owner Command" },
};

// Demo-mode readiness telemetry — synthetic but credible.
// In a live deployment these would be DB-driven from completion records.
const READINESS_DEMO = {
  certifiedPct: 87,
  bilingualPct: 92,
  activeOnboarding: 14,
  overdue: 3,
  lessonsCompleted: 142,
  qrPostersDeployed: 8,
};

function trackUnlocked(track) {
  if (!track) return false;
  if (track.audience === "public") return true;
  if (isDemoMode()) return true;
  if (isAdmin()) return true;
  if (track.audience === "pm") return isPm();
  if (track.audience === "shop") return isShop() || isPm();
  return false;
}

function loginPathFor(audience) {
  if (audience === "admin") return "/admin/login";
  if (audience === "pm") return "/pm/login";
  if (audience === "shop") return "/shop/login";
  return "/";
}

export default function TrainingHub() {
  const tenant = useTenant();
  const { t, lang } = useT();
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const i = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(i);
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "#0A0A0A", color: "#FFFFFF" }}>
      {/* Caution stripe — matches DemoLanding */}
      <div className="caution-stripe" />

      {/* Header — dark industrial, mark logo + back link, identical to admin/pm/shop */}
      <header style={{ background: "#0B0B0B", borderBottom: "1px solid #1A1A1A" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between gap-3">
          <Link
            to="/"
            className="inline-flex items-center text-white text-xs font-bold uppercase tracking-[0.25em] hover:text-orange-400 transition-colors"
            data-testid="training-back-hub"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> {t("Platform")}
          </Link>
          <MasciLogo variant="mark" size="md" homeLink="/" />
          <LangToggle />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        {/* Hero — Workforce Readiness Command Center */}
        <div className="mb-10 sm:mb-12 max-w-4xl relative">
          {/* Ember-glow halo */}
          <div
            aria-hidden
            className="absolute pointer-events-none"
            style={{
              top: -40, left: -40, width: 280, height: 280, borderRadius: 9999,
              filter: "blur(100px)",
              background: "radial-gradient(circle, rgba(255,106,0,0.16) 0%, rgba(255,106,0,0) 70%)",
            }}
          />
          <div className="relative">
            <div className="font-mono text-[11px] uppercase tracking-[0.4em] mb-3 flex items-center gap-2"
                 style={{ color: "#FF6A00" }}>
              <GraduationCap className="w-3.5 h-3.5" />
              Workforce Readiness · Command Center
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white">
              {lang === "es" ? (
                <>Su cuadrilla, lista — <span style={{ color: "#FF6A00" }}>en cualquier idioma, en cualquier dispositivo.</span></>
              ) : (
                <>Your crew, certified — <span style={{ color: "#FF6A00" }}>in any language, on any device.</span></>
              )}
            </h1>
            <p className="text-base sm:text-lg mt-4 leading-relaxed max-w-2xl"
               style={{ color: "#BDBDBD" }}>
              {t("Field-forged operational readiness for every role — Field Crews, Shop, Project Managers, and Owners. Bilingual lessons, QR-deployed in the trailer, completions auto-routed to crew profiles.")}
            </p>
          </div>
        </div>

        {/* Live readiness telemetry strip */}
        <div
          className="grid grid-cols-2 lg:grid-cols-6 gap-px mb-12 rounded-md overflow-hidden"
          style={{ background: "#1A1A1A", border: "1px solid #1A1A1A" }}
          data-testid="training-readiness-strip"
        >
          <KpiTile
            icon={Award}
            label="Certified"
            value={`${READINESS_DEMO.certifiedPct}%`}
            tone="ok"
          />
          <KpiTile
            icon={Languages}
            label="Bilingual ready"
            value={`${READINESS_DEMO.bilingualPct}%`}
            tone="ok"
          />
          <KpiTile
            icon={Users}
            label="Onboarding · active"
            value={READINESS_DEMO.activeOnboarding}
            tone="info"
          />
          <KpiTile
            icon={Clock}
            label="Overdue"
            value={READINESS_DEMO.overdue}
            tone={READINESS_DEMO.overdue > 0 ? "warn" : "ok"}
          />
          <KpiTile
            icon={CheckCircle2}
            label="Lessons completed · 30d"
            value={READINESS_DEMO.lessonsCompleted}
            tone="ok"
          />
          <KpiTile
            icon={QrCode}
            label="QR posters live"
            value={READINESS_DEMO.qrPostersDeployed}
            tone="info"
          />
        </div>

        {/* System status row — like Operations Command */}
        <div
          className="flex items-center justify-between gap-4 flex-wrap mb-6 px-4 py-3 rounded-md"
          style={{ background: "#0F0F0F", border: "1px solid #1A1A1A" }}
        >
          <div className="flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] font-bold"
                  style={{ color: "#65DA76" }}>
              <span className="w-2 h-2 rounded-full" style={{ background: "#65DA76", boxShadow: "0 0 8px #65DA76" }} />
              SYSTEM HEALTHY
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: "#8A8A8A" }}>
              READINESS TRACKS · LIVE
            </span>
          </div>
          <span className="font-mono text-xs" style={{ color: "#FF6A00" }}>
            {now.toLocaleTimeString([], { hour12: false })}
          </span>
        </div>

        {/* Readiness Tracks — operational, not "courses" */}
        <div className="mb-6">
          <div className="font-mono text-[11px] uppercase tracking-[0.4em] mb-3" style={{ color: "#FF6A00" }}>
            Operational Readiness Tracks
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tight text-white">
            Pick a role. Deploy the readiness track.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px mb-12 rounded-md overflow-hidden"
             style={{ background: "#1A1A1A", border: "1px solid #1A1A1A" }}>
          {Object.values(TRACKS).map((track) => {
            const Icon = ICONS[track.icon] || GraduationCap;
            const lessons = lessonsForTrack(track.slug);
            const unlocked = trackUnlocked(track);
            const ops = TRACK_OPS_LABEL[track.slug] || { eyebrow: track.audience.toUpperCase(), title: track.title };
            const destination = unlocked
              ? `/training/${track.slug}`
              : loginPathFor(track.audience);
            // Demo synthetic completion mix — illustrative
            const compl = { field: 92, shop: 78, pm: 64, admin: 81 }[track.slug] || 70;
            return (
              <Link
                key={track.slug}
                to={destination}
                className="group relative p-6 sm:p-7 transition-colors"
                style={{ background: "#0F0F0F" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#141414")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "#0F0F0F")}
                data-testid={`training-track-${track.slug}`}
              >
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="flex items-start gap-3">
                    <span
                      className="inline-flex items-center justify-center w-12 h-12 rounded-md shrink-0"
                      style={{
                        background: "rgba(255,106,0,0.14)",
                        color: "#FF6A00",
                        border: "1px solid rgba(255,106,0,0.4)",
                      }}
                    >
                      <Icon className="w-6 h-6" />
                    </span>
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.3em]" style={{ color: "#FF6A00" }}>
                        {ops.eyebrow}
                      </div>
                      <div className="font-display text-2xl sm:text-3xl font-black tracking-tight text-white leading-tight mt-0.5">
                        {ops.title}
                      </div>
                    </div>
                  </div>
                  <span
                    className="font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-1 rounded shrink-0"
                    style={{ background: "#1A1A1A", color: "#BDBDBD", border: "1px solid #2A2A2A" }}
                  >
                    {lessons.length} lessons
                  </span>
                </div>

                <p className="text-sm leading-relaxed mb-4" style={{ color: "#BDBDBD" }}>
                  {lang === "es" && track.blurb_es ? track.blurb_es : track.blurb}
                </p>

                {/* Readiness completion bar */}
                <div className="mb-4">
                  <div className="flex justify-between mb-1.5">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: "#8A8A8A" }}>
                      Crew readiness
                    </span>
                    <span className="font-mono text-[10px] font-bold" style={{ color: compl >= 80 ? "#65DA76" : "#FFD000" }}>
                      {compl}%
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "#1A1A1A" }}>
                    <div
                      className="h-full transition-all"
                      style={{
                        width: `${compl}%`,
                        background: compl >= 80 ? "#2E7D32" : "#FFB000",
                      }}
                    />
                  </div>
                </div>

                {/* Top lessons */}
                <ul className="space-y-1 mb-4">
                  {lessons.slice(0, 3).map((l) => (
                    <li key={l.slug} className="flex items-start gap-2 text-xs" style={{ color: "#E0E0E0" }}>
                      <span className="mt-1.5 w-1 h-1 rounded-full shrink-0" style={{ background: "#FF6A00" }} />
                      <span className="truncate">{l.title.replace(/^Lesson \d+ — /, "")}</span>
                    </li>
                  ))}
                  {lessons.length > 3 && (
                    <li className="text-[11px] pl-3 italic" style={{ color: "#666" }}>
                      + {lessons.length - 3} more readiness lessons
                    </li>
                  )}
                </ul>

                <div className="flex items-center justify-between pt-4 border-t" style={{ borderColor: "#1A1A1A" }}>
                  <div className="flex items-center gap-1.5">
                    <Chip><Languages className="w-3 h-3" /> EN / ES</Chip>
                    <Chip><QrCode className="w-3 h-3" /> QR</Chip>
                    <Chip><FileText className="w-3 h-3" /> Cert</Chip>
                  </div>
                  <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.25em] font-bold"
                        style={{ color: "#FF6A00" }}>
                    Open track <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Capability band — matching DemoLanding */}
        <div
          className="grid grid-cols-2 lg:grid-cols-4 gap-px rounded-md overflow-hidden mb-10"
          style={{ background: "#1A1A1A", border: "1px solid #1A1A1A" }}
        >
          <Capability
            icon={Languages}
            label="Bilingual native"
            copy="Every lesson EN + ES side-by-side. No translation friction."
          />
          <Capability
            icon={QrCode}
            label="QR onboarding"
            copy="Tape a poster in the trailer. Crews scan straight into the right packet."
          />
          <Capability
            icon={PlayCircle}
            label="Video + walk-through"
            copy="MP4 / YouTube / Loom embedded. Print-friendly cheat sheets included."
          />
          <Capability
            icon={CheckCircle2}
            label="Auto-routed completions"
            copy="Completions land in the operator's profile and the company's audit-ready pack."
          />
        </div>

        {/* Footer band — white-label / proprietary positioning */}
        <div
          className="p-5 sm:p-6 rounded-md flex items-center justify-between gap-5 flex-wrap"
          style={{ background: "#0F0F0F", border: "1px solid #1A1A1A" }}
          data-testid="training-whitelabel-band"
        >
          <div className="flex items-center gap-3">
            <span
              className="inline-flex items-center justify-center w-12 h-12 rounded-md shrink-0"
              style={{
                background: "rgba(255,106,0,0.14)",
                color: "#FF6A00",
                border: "1px solid rgba(255,106,0,0.4)",
              }}
            >
              <Activity className="w-6 h-6" />
            </span>
            <div>
              <div className="font-display font-black text-white">Authored, branded, and routed for your company.</div>
              <div className="text-xs mt-0.5" style={{ color: "#BDBDBD" }}>
                Proprietary operational systems · field-forged · built for contractors who run heavy civil work.
              </div>
            </div>
          </div>
          <span
            className="font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-2 rounded"
            style={{
              background: "rgba(255,106,0,0.14)",
              color: "#FF6A00",
              border: "1px solid rgba(255,106,0,0.4)",
            }}
          >
            {(tenant.brand_name || "FORGEDOPS").toUpperCase()} · WHITE-LABEL READY
          </span>
        </div>
      </main>
    </div>
  );
}

function Chip({ children }) {
  return (
    <span
      className="font-mono text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 rounded inline-flex items-center gap-1"
      style={{ background: "#1A1A1A", color: "#FFFFFF", border: "1px solid #2A2A2A" }}
    >
      {children}
    </span>
  );
}

function KpiTile({ icon: Icon, label, value, tone = "info" }) {
  const accent =
    tone === "ok" ? "#65DA76" :
    tone === "warn" ? "#FFD000" :
    tone === "bad" ? "#FF6A6A" :
    "#FF6A00";
  return (
    <div className="p-4 sm:p-5" style={{ background: "#0F0F0F" }} data-testid={`training-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
      <div className="flex items-center gap-2 mb-2">
        <span
          className="inline-flex items-center justify-center w-8 h-8 rounded"
          style={{
            background: `${accent}1F`,
            color: accent,
            border: `1px solid ${accent}55`,
          }}
        >
          <Icon className="w-4 h-4" />
        </span>
      </div>
      <div className="font-display text-2xl sm:text-3xl font-black tracking-tight text-white leading-none">
        {value}
      </div>
      <div className="font-mono text-[9px] uppercase tracking-[0.25em] mt-1.5" style={{ color: "#8A8A8A" }}>
        {label}
      </div>
    </div>
  );
}

function Capability({ icon: Icon, label, copy }) {
  return (
    <div className="p-4 sm:p-5" style={{ background: "#0F0F0F" }}>
      <span
        className="inline-flex items-center justify-center w-9 h-9 rounded-md mb-3"
        style={{
          background: "rgba(255,106,0,0.12)",
          color: "#FF6A00",
          border: "1px solid rgba(255,106,0,0.3)",
        }}
      >
        <Icon className="w-4 h-4" />
      </span>
      <div className="font-display font-black text-white text-sm mb-1">{label}</div>
      <div className="text-xs leading-relaxed" style={{ color: "#BDBDBD" }}>{copy}</div>
    </div>
  );
}
